import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { BaseUrl } from "../config/api";
import offerLogo from "../assets/images/offer-logo.svg";
import "../assets/styles/ProductDetail.css";
import { getAccessoriesByProduct } from "../service/accessoryService";
import { addToRecentlyViewed } from "../utils/productUtils";
import { useCart } from "../context/CartContext";
import Cookies from "js-cookie";
import { toHttps } from "../utils/imageUtils";
import ProductReviews from "../components/ProductReviews";
import { isNewProduct } from "../utils/dateUtils";

const safeParse = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};


const ProductSliderSection = ({ title, products }) => {
  const sliderRef = useRef(null);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 300;
      if (direction === "left") {
        sliderRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }
  };

  if (!products || products.length === 0) return null;

  return (
    <div className="related-products-section">
      <div className="hr-container">
        <hr />
      </div>
      <div className="container">
        <h2>{title}</h2>
        <div className="product-slider-wrapper">
          <button
            className="product-slider-arrow left"
            onClick={() => scroll("left")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="product-slider-container" ref={sliderRef}>
            {products.map((product) => {
              const isAccessory = Boolean(product.isAccessory);
              const card = (
                <div className="product-card-container">
                  <div className="product-image-container">
                    <img
                      src={product.images && product.images[0]}
                      alt={product.name}
                      className="primary-image"
                      loading="lazy"
                    />
                    <img
                      src={
                        (product.images && product.images[1]) ||
                        (product.images && product.images[0])
                      }
                      alt={product.name}
                      className="secondary-image"
                      loading="lazy"
                    />
                    {product.discount > 0 && (
                      <span className="discount-tag">
                        {product.discount}% OFF
                      </span>
                    )}
                  </div>
                  <div className="product-info">
                    <h3>{product.name}</h3>
                    <p className="price">{product.price || "$0.00 AUD"}</p>
                  </div>
                </div>
              );

              if (isAccessory) {
                const targetPath = product.linkedProductId
                  ? `/product/${product.linkedProductId}`
                  : "/collections/accessories";
                return (
                  <Link
                    key={product.id || product._id}
                    to={targetPath}
                    className="product-card"
                    onClick={() => {
                      window.scrollTo(0, 0);
                    }}
                  >
                    {card}
                  </Link>
                );
              }

              return (
                <Link
                  key={product.id || product._id}
                  to={`/product/${product._id || product.id}`}
                  className="product-card"
                  onClick={() => {
                    window.scrollTo(0, 0);
                  }}
                >
                  {card}
                </Link>
              );
            })}
          </div>
          <button
            className="product-slider-arrow right"
            onClick={() => scroll("right")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductDetail = () => {
  const { productId } = useParams();

  // Always scroll to top when navigating to a product page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  const [selectedSize, setSelectedSize] = useState("OneSize");
  const [selectedImage, setSelectedImage] = useState(0);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const productInfoRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  const [activeTab, setActiveTab] = useState("product-details");
  const [similarProducts, setSimilarProducts] = useState([]);
  const [completeLookProducts, setCompleteLookProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [wornAdoredProducts, setWornAdoredProducts] = useState([]);
  const { addToCart, isInCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChartSize, setSelectedChartSize] = useState(null);

  useEffect(() => {
    if (product) {
      if (product.availableSizes && product.availableSizes.length > 0) {
        setSelectedChartSize(product.availableSizes[0]);
      } else {
        setSelectedChartSize("M");
      }
    }
  }, [product]);
  const [showMeasureModal, setShowMeasureModal] = useState(false);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [sizeUnit, setSizeUnit] = useState("cm"); // Default unit is cm as per user request
  const zoomContainerRef = useRef(null);
  const zoomLevelRef = useRef(1);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0, show: false });
  const mainImageRef = useRef(null);
  const [hoveredThumbnail, setHoveredThumbnail] = useState(null);
  const [activeColorImages, setActiveColorImages] = useState(null);
  const [activeColorName, setActiveColorName] = useState(null);



  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${BaseUrl}/products/getproduct/${productId}`
        );
        if (!response.ok) {
          throw new Error("Product not found");
        }
        const data = await response.json();

        // Debug log to see raw data
        console.log("Raw data from API:", data);
        console.log("Actual Price from API:", data.actualPrice);
        console.log("Selling Price from API:", data.sellingPrice);

        // Transform the data to match your frontend structure
        const normalizeColorImages = (items) =>
          (Array.isArray(items) ? items : [])
            .map((item) => {
              const images = [];
              if (Array.isArray(item?.images)) images.push(...item.images);
              if (item?.image) images.push(item.image);
              return {
                color: item?.color || "",
                images: images.filter(Boolean).map(toHttps),
              };
            })
            .filter((item) => item.images.length > 0 || item.color);

        const transformedProduct = {
          id: data._id,
          _id: data._id,
          name: data.productName,
          // TEST: Set to $10 for checkout testing
          actualPrice: 12,
          sellingPrice: 10,
          price: `$10.00 AUD`,
          images:
            data.images && data.images.length > 0
              ? data.images.map(toHttps)
              : [toHttps(data.categoryId?.categotyImage)],
          fabric: data.materialAndCare || "Not specified",
          color: data.color || "Not specified",
          size: data.sizeAndFit || "FREE SIZE",
          type: data.categoryName,
          dateAdded: data.createdAt,

          discount: calculateDiscount(12, 10), // TEST: 17% discount
          description: data.description || "",
          details: data.details || "",
          stock: Number(data.stock || 0),
          availability: data.availability,
          productCode: data.productCode,
          tags: data.tags || [],
          tax: Number(data.tax || 0),
          couponCode: data.couponCode,
          couponMethod: data.couponMethod,
          variantName: data.variantName || "",
          categoryId: data.categoryId?._id,
          categoryType: data.categoryId?.categoryType,
          productCategory: data.categoryName,
          materialAndCare: data.materialAndCare || "Not specified",
          colorImages: normalizeColorImages(data.colorImages),
          sareeSize: data.sareeSize ? `${data.sareeSize} meters` : null,
          blouseSize: data.blouseSize ? `${data.blouseSize} meters` : null,
          sizeAndFit: data.sizeAndFit || "FREE SIZE",
          availableSizes: data.availableSizes || [],
          displaySizeTable: data.displaySizeTable ?? false,
          sizeChartHeaders: data.sizeChartHeaders || [],
          sizeChartData: data.sizeChartData || [],
          measurements: data.measurements || [],
        };

        // Debug log transformed data
        console.log("Transformed product:", transformedProduct);
        console.log(
          "Transformed actual price:",
          transformedProduct.actualPrice
        );
        console.log(
          "Transformed selling price:",
          transformedProduct.sellingPrice
        );

        setProduct(transformedProduct);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching product details:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  useEffect(() => {
    setActiveColorImages(null);
    setActiveColorName(null);
    setSelectedImage(0);
  }, [productId]);

  useEffect(() => {
    if (product) {
      setIsWishlisted(isInWishlist(product.id));
      const recentlyViewedIds = safeParse(Cookies.get("recentlyViewed"), []);

      // Add product to recently viewed
      addToRecentlyViewed(product.id);

      // Fetch recently viewed products
      const fetchRecentlyViewed = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const allProducts = await response.json();

          const recentProducts = recentlyViewedIds
            .map((id) => allProducts.find((p) => p._id === id))
            .filter((p) => p && p._id !== product.id)
            .map(transformProductData)
            .slice(0, 6);

          setRecentlyViewed(recentProducts);
        } catch (error) {
          console.error("Error fetching recently viewed products:", error);
          setRecentlyViewed([]);
        }
      };

      fetchRecentlyViewed();

      // Fetch similar products from the same category
      const fetchSimilarProducts = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const data = await response.json();
          const filtered = data
            .filter(
              (p) =>
                p.categoryName === product.type &&
                p._id !== product.id &&
                !p.isDeleted
            )
            .slice(0, 5)
            .map((p) => ({
              id: p._id,
              _id: p._id,
              name: p.productName,
              price: `$${Number(p.sellingPrice).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} AUD`,
              color: p.color,
              images:
                p.images && p.images.length > 0
                  ? p.images.map(toHttps)
                  : [toHttps(p.categoryId?.categotyImage)],
              discount: calculateDiscount(p.actualPrice, p.sellingPrice),
            }));
          setSimilarProducts(filtered);
          console.log("Similar products:", filtered);
        } catch (error) {
          console.error("Error fetching similar products:", error);
        }
      };

      fetchSimilarProducts();
    }
  }, [product, isInWishlist]);

  useEffect(() => {
    if (product && product.variantId) {
      const fetchVariantProducts = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const data = await response.json();
          const variantProducts = data
            .filter(
              (p) =>
                p.variantId === product.variantId &&
                p._id !== product.id &&
                !p.isDeleted
            )
            .map(transformProductData);
          setSimilarProducts(variantProducts);
        } catch (error) {
          console.error("Error fetching variant products:", error);
        }
      };

      fetchVariantProducts();
    }
  }, [product]);

  useEffect(() => {
    if (product && product.variantName) {
      const fetchSimilarProducts = async () => {
        try {
          const response = await fetch(`${BaseUrl}/products/getallproducts`);
          const data = await response.json();
          const similarVariants = data
            .filter(
              (p) =>
                p.variantName === product.variantName &&
                p._id !== product._id &&
                !p.isDeleted
            )
            .map((p) => ({
              id: p._id,
              _id: p._id,
              name: p.productName,
              price: `$${p.sellingPrice.toLocaleString()} AUD`,
              color: p.color,
              images:
                p.images && p.images.length > 0
                  ? p.images.map(toHttps)
                  : [toHttps(p.categoryId?.categotyImage)],
              discount: calculateDiscount(p.actualPrice, p.sellingPrice),
            }));
          setSimilarProducts(similarVariants);
        } catch (error) {
          console.error("Error fetching similar products:", error);
        }
      };

      fetchSimilarProducts();
    }
  }, [product]);

  useEffect(() => {
    if (!productId) return;

    const fetchAccessories = async () => {
      try {
        const accessories = await getAccessoriesByProduct(productId);
        const validApiAccessories = Array.isArray(accessories)
          ? accessories.filter(
            (item) =>
              item &&
              Array.isArray(item.images) &&
              item.images.length > 0 &&
              item.images[0]
          )
          : [];
        setCompleteLookProducts(validApiAccessories);
      } catch (error) {
        console.error("Accessory API error:", error);
        setCompleteLookProducts([]);
      }
    };

    fetchAccessories();
  }, [productId]);

  useEffect(() => {
    if (!productId) return;

    const fetchCatalogProducts = async () => {
      try {
        const response = await fetch(`${BaseUrl}/products/getallproducts`);
        if (!response.ok) {
          throw new Error("Failed to fetch catalog products");
        }

        const data = await response.json();
        const transformedCatalog = data
          .filter((item) => !item.isDeleted && item.stock > 0 && !item.outOfStock)
          .map(transformProductData);

        setCatalogProducts(transformedCatalog);
      } catch (err) {
        console.error("Error fetching catalog products:", err);
        setCatalogProducts([]);
      }
    };

    fetchCatalogProducts();
  }, [productId]);

  useEffect(() => {
    if (!product) return;

    const excludedIds = new Set([
      getNormalizedId(product),
      ...recentlyViewed.map(getNormalizedId),
      ...similarProducts.map(getNormalizedId),
      ...completeLookProducts.map(getNormalizedId),
    ]);

    const remainingProducts = catalogProducts
      .filter((item) => {
        const id = getNormalizedId(item);
        return id && !excludedIds.has(id);
      })
      .slice(0, 10);

    setWornAdoredProducts(remainingProducts);
  }, [catalogProducts, completeLookProducts, product, recentlyViewed, similarProducts]);

  const calculateDiscount = (actualPrice, sellingPrice) => {
    // Debug log the inputs
    console.log("Calculating discount with:", { actualPrice, sellingPrice });

    const actual = parseFloat(actualPrice);
    const selling = parseFloat(sellingPrice);

    if (isNaN(actual) || isNaN(selling) || actual <= 0) {
      console.log("Invalid price values for discount calculation");
      return 0;
    }

    const discount = ((actual - selling) / actual) * 100;
    const roundedDiscount = Math.round(discount);

    // Debug log the result
    console.log("Calculated discount:", roundedDiscount);

    return roundedDiscount;
  };

  const transformProductData = (data) => ({
    id: data._id || data.id,
    name: data.productName,
    price: `$${Number(data.sellingPrice || 0).toFixed(2)} AUD`,
    images:
      data.images && data.images.length > 0
        ? data.images.map(toHttps)
        : [toHttps(data.categoryId?.categotyImage)],
    type: data.categoryName,
    discount: calculateDiscount(
      Number(data.actualPrice || 0),
      Number(data.sellingPrice || 0)
    ),
    _id: data._id,
    availableSizes: data.availableSizes || [],
    measurements: data.measurements || [],
    actualPrice: Number(data.actualPrice || 0),
    sellingPrice: Number(data.sellingPrice || 0),
  });

  const formatCategoryName = (name) => {
    return name
      ? name
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
      : "";
  };

  function getNormalizedId(item) {
    return String(item?._id || item?.id || "");
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error || !product) {
    return (
      <div className="product-detail-container">
        <div className="breadcrumb">
          <Link to="/">Home</Link>
        </div>
        <div className="product-not-found">
          <h2>Product not found</h2>
          <Link to="/" className="back-home">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const baseImages =
    product.images || [product.image, product.image2].filter(Boolean);
  const normalizedBaseImages = Array.isArray(baseImages)
    ? baseImages.filter(Boolean)
    : [];
  const displayImages =
    activeColorImages && activeColorImages.length > 0
      ? activeColorImages
      : normalizedBaseImages;
  const productImages = displayImages; // backward compatibility for any remaining references
  const productOffers = product.offers || [];
  const categoryPath = product.categoryPath || "";
  const displayedColorName = activeColorName || product.color || "Default";
  const colorImageVariants = Array.isArray(product.colorImages)
    ? product.colorImages.filter(
      (item) =>
        item &&
        Array.isArray(item.images) &&
        item.images.length > 0
    )
    : [];
  const baseThumbnail = normalizedBaseImages[0];
  const hasBaseThumbnail =
    baseThumbnail &&
    colorImageVariants.some((item) => item?.images?.[0] === baseThumbnail);
  const mergedColorVariants =
    baseThumbnail && !hasBaseThumbnail
      ? [
        {
          color: product.color || "Default",
          images: normalizedBaseImages,
          isDefault: true,
        },
        ...colorImageVariants,
      ]
      : colorImageVariants;

  const handleWishlist = (e) => {
    e.preventDefault();

    if (isWishlisted) {
      removeFromWishlist(parseInt(productId));
      setIsWishlisted(false);
    } else {
      addToWishlist(product);
      setIsWishlisted(true);
    }
  };

  const handlePrevImage = () => {
    if (!displayImages.length) return;
    setSelectedImage((prev) =>
      prev === 0 ? displayImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    if (!displayImages.length) return;
    setSelectedImage((prev) =>
      prev === displayImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleColorSelect = (colorItem) => {
    const colorImages = Array.isArray(colorItem?.images)
      ? colorItem.images.filter(Boolean)
      : [];
    if (colorImages.length === 0) return;

    setActiveColorImages(colorImages);
    setActiveColorName(colorItem?.color || null);
    setSelectedImage(0);
  };

  const handleMagnifierMove = (e) => {
    if (!mainImageRef.current) return;

    // Prevent scrolling when touching the image to zoom
    if (e.type === 'touchmove' || e.type === 'touchstart') {
      if (e.cancelable) e.preventDefault();
    }
    const { left, top, width, height } = mainImageRef.current.getBoundingClientRect();

    let clientX, clientY;
    if (e.type.startsWith('touch')) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Position relative to the image
    let x = clientX - left;
    let y = clientY - top;

    // Lens dimensions (must match CSS)
    const lensWidth = 180;
    const lensHeight = 230;

    // Boundary check for lens
    if (x < lensWidth / 2) x = lensWidth / 2;
    if (x > width - lensWidth / 2) x = width - lensWidth / 2;
    if (y < lensHeight / 2) y = lensHeight / 2;
    if (y > height - lensHeight / 2) y = height - lensHeight / 2;

    // Calculate background position percentages
    const xPercent = ((x - lensWidth / 2) / (width - lensWidth)) * 100;
    const yPercent = ((y - lensHeight / 2) / (height - lensHeight)) * 100;

    setMagnifierPos({
      x: x - lensWidth / 2,
      y: y - lensHeight / 2,
      xPercent,
      yPercent,
      show: true
    });
  };

  const handleMagnifierLeave = () => {
    setMagnifierPos((prev) => ({ ...prev, show: false }));
  };

  const formatPrice = (priceStr) => {
    const numericPrice = parseFloat(priceStr.replace("$", "").replace(",", ""));
    return `$${numericPrice.toLocaleString()}`;
  };

  const calculateInflatedPrice = (price, discount) => {
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""));
    const inflatedPrice = Math.round(
      numericPrice + (numericPrice * discount) / 100
    );
    return `$${inflatedPrice.toLocaleString()}`;
  };

  const getOfferDetails = (product) => {
    const offers = [
      {
        title: "BANK OFFER",
        description: "10% Instant Discount on HDFC Bank Credit Cards",
      },
    ];

    if (product.discount) {
      offers.push({
        title: "ADDITIONAL FLAT 20% OFF",
        description: "Products Are Pre Discounted For You!",
      });
    }

    if (product.offer === "Buy1Get1") {
      offers.push({
        title: "BUY 2 GET 1 FREE",
        description: "Add 3 Products To Your Cart And Get 1 For Free!",
      });
    }

    offers.push({
      title: "SPECIAL PRICE",
      description: "Get extra ₹2000 off (price inclusive of discount)",
    });

    if (product.offer?.title && product.offer?.description) {
      offers.push({
        title: product.offer.title,
        description: product.offer.description,
      });
    }

    return offers;
  };

  const offerDetails = getOfferDetails(product);



  return (
    <>
      <div className="product-detail-container">
        <div className="breadcrumb">
          <Link to="/">Home</Link> /{" "}
          {product.productCategory && (
            <>
              <Link
                to={`/category/${product.productCategory
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {formatCategoryName(product.productCategory)}
              </Link>{" "}
              /{" "}
            </>
          )}
          <span>{product.name}</span>
        </div>

        <div className="product-detail-content">
          <div className="product-images">
            <div className="thumbnail-list">
              {displayImages.map((image, index) => (
                <div
                  key={index}
                  className={`thumbnail ${selectedImage === index ? "active" : ""
                    }`}
                  onClick={() => setSelectedImage(index)}
                  onMouseEnter={() => setHoveredThumbnail(index)}
                  onMouseLeave={() => setHoveredThumbnail(null)}
                  style={{ position: 'relative' }}
                >
                  <img src={image} alt={`Product view ${index + 1}`} />
                  {hoveredThumbnail === index && (
                    <div className="thumbnail-preview-popup">
                      <img src={image} alt={`Preview ${index + 1}`} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                position: "relative",
                margin: "0",
              }}
            >
              <div className="main-image">
                <button className="nav-button prev" onClick={handlePrevImage}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <img
                  ref={mainImageRef}
                  src={displayImages[selectedImage]}
                  alt={product.name}
                  onMouseMove={handleMagnifierMove}
                  onMouseLeave={handleMagnifierLeave}
                  onTouchMove={handleMagnifierMove}
                  onTouchStart={handleMagnifierMove}
                  onTouchEnd={handleMagnifierLeave}
                />
                {magnifierPos.show && (
                  <div
                    className="magnifier-lens"
                    style={{
                      left: `${magnifierPos.x}px`,
                      top: `${magnifierPos.y}px`,
                    }}
                  >
                    <div className="lens-label">{product.name}</div>
                  </div>
                )}
                <button className="nav-button next" onClick={handleNextImage}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <button
                  className="zoom-button"
                  onClick={() => setShowZoomModal(true)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>
              {magnifierPos.show && (
                <div
                  className="magnifier-zoom-window"
                  style={{
                    backgroundImage: `url(${displayImages[selectedImage]})`,
                    backgroundPosition: `${magnifierPos.xPercent}% ${magnifierPos.yPercent}%`,
                    backgroundSize: `${(mainImageRef.current?.width || 0) * 3}px auto`, // Zoom level 3x
                  }}
                />
              )}
              <div className="product-note">
                <p>
                  <strong>Note:</strong> Product Colour/Texture/Pattern May Slightly Differ Due to Photographic Lighting Sources Or Your Monitor Settings.
                </p>
              </div>
            </div>
          </div>

          <div className="product-detail-info" ref={productInfoRef}>
            <div className="product-header">
              <h1>{product.name}</h1>
            </div>
            <div className="price-section">
              <div className="price-details">
                {product.discount > 0 ? (
                  <>
                    <span className="current-price">
                      ${parseFloat(product.sellingPrice).toFixed(2)} <span className="currency-suffix">AUD</span>
                    </span>
                    <span className="original-price">
                      ${parseFloat(product.actualPrice).toFixed(2)} <span className="currency-suffix">AUD</span>
                    </span>
                    <span className="discount">
                      (
                      {calculateDiscount(
                        product.actualPrice,
                        product.sellingPrice
                      )}
                      % OFF)
                    </span>
                  </>
                ) : (
                  <span className="current-price">
                    ${parseFloat(product.sellingPrice).toFixed(2)} <span className="currency-suffix">AUD</span>
                  </span>
                )}
              </div>
              <div className="inclusive-text">Inclusive of all taxes</div>
            </div>

            {product.description && (
              <div className="description-section">
                <div
                  className="description-html"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}

            <div className="color-section">
              <h3>Available Colours</h3>
              <div className="color-variants">
                {mergedColorVariants.length > 0 ? (
                  <div className="variant-thumbnails">
                    {mergedColorVariants.map((item, index) => (
                      <div
                        key={`${item.color || "color"}-${index}`}
                        className="variant-thumbnail"
                        onClick={() => handleColorSelect(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleColorSelect(item);
                          }
                        }}
                      >
                        <img
                          src={item.images[0]}
                          alt={item.color || "Product color"}
                        />
                        <span className="color-name">
                          {item.color || "Default"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : product.variantName && similarProducts.length > 0 ? (
                  <div className="variant-thumbnails">
                    {similarProducts.slice(0, 10).map((item) => (
                      <Link
                        key={item._id}
                        to={`/product/${item._id || item.id}`}
                        className="variant-thumbnail"
                        onClick={() => {
                          window.scrollTo(0, 0);
                          console.log("Product ID:", item._id || item.id);
                        }}
                      >
                        <img
                          src={item.images[0]}
                          alt={item.color || "Product variant"}
                        />
                        <span className="color-name">
                          {item.color || "Default"}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="single-color">
                    <div className="color-thumbnail">
                      <img
                        src={displayImages[0]}
                        alt={product.color || "Product color"}
                      />
                      <span className="color-name">
                        {activeColorName || product.color || "Default"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {product.displaySizeTable && product.categoryType !== "Saree" && (
              <div className="size-selection-container">
                <div className="size-chart-header">
                  <h3>Size Chart</h3>
                  <div className="unit-selector">
                    <button 
                      className={`unit-btn ${sizeUnit === "inches" ? "active" : ""}`}
                      onClick={() => setSizeUnit("inches")}
                    >
                      INCHES
                    </button>
                    <button 
                      className={`unit-btn ${sizeUnit === "cm" ? "active" : ""}`}
                      onClick={() => setSizeUnit("cm")}
                    >
                      CM
                    </button>
                  </div>
                </div>

                {/* Dynamic size chart from admin */}
                {product.sizeChartHeaders && product.sizeChartHeaders.length > 0 && product.sizeChartData && product.sizeChartData.length > 0 ? (
                  <div className="size-measurement-table">
                    <table>
                      <thead>
                        <tr>
                          {product.sizeChartHeaders.map((header, idx) => (
                            <th key={idx} dangerouslySetInnerHTML={{ __html: header.replace(/\n/g, '<br/>') }} />
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {product.sizeChartData.map((row, rIdx) => (
                          <tr key={rIdx}>
                            {row.map((cell, cIdx) => {
                              // If unit is inches, try to convert numeric values from cm
                              let displayValue = cell;
                              if (sizeUnit === "inches" && cIdx !== 0) { // Skip "Size" column (usually first)
                                const numericValue = parseFloat(cell);
                                if (!isNaN(numericValue)) {
                                  displayValue = (numericValue / 2.54).toFixed(1);
                                }
                              }
                              return <td key={cIdx}>{displayValue}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-2">Size chart not available for this product.</p>
                )}

                <div className="how-to-measure-container">
                  <button className="how-to-measure-btn" onClick={() => setShowMeasureModal(true)}>
                    How to measure
                  </button>
                </div>
              </div>
            )}


            <div className="action-buttons">
              <button
                className={`add-to-cart ${isInCart(product.id) ? "in-cart" : ""
                  }`}
                onClick={() => addToCart(product)}
                disabled={isInCart(product.id)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  <line x1="12" y1="6" x2="12" y2="15" />
                  <line x1="17" y1="6" x2="17" y2="15" />
                  <circle cx="9" cy="21" r="1" fill="currentColor" />
                  <circle cx="20" cy="21" r="1" fill="currentColor" />
                  <g transform="translate(19, 5)">
                    <circle cx="0" cy="0" r="5" fill="white" stroke="currentColor" strokeWidth="2" />
                    <path d="M-3 0h6M0 -3v6" stroke="currentColor" strokeWidth="2" />
                  </g>
                </svg>
                {isInCart(product.id) ? "In Cart" : "Add to Cart"}
              </button>
              <button
                className={`wishlist ${isWishlisted ? "wishlist-active" : ""}`}
                onClick={handleWishlist}
              >
                {isWishlisted ? "Wishlisted" : "Wishlist"}
              </button>
            </div>
            {/* Tabbed Product Details Table */}
            <div className="product-details-table">
              <div className="product-details-tabs">
                <button
                  className={`tab-button ${activeTab === "product-details" ? "active" : ""}`}
                  onClick={() => setActiveTab("product-details")}
                >
                  Product Details
                </button>
                <button
                  className={`tab-button ${activeTab === "size-fit" ? "active" : ""}`}
                  onClick={() => setActiveTab("size-fit")}
                >
                  Size & Fit
                </button>
                <button
                  className={`tab-button ${activeTab === "wash-care" ? "active" : ""}`}
                  onClick={() => setActiveTab("wash-care")}
                >
                  Wash Care
                </button>
                <button
                  className={`tab-button ${activeTab === "shipping" ? "active" : ""}`}
                  onClick={() => setActiveTab("shipping")}
                >
                  Shipping & Delivery
                </button>
              </div>
              <div className="tab-content">
                {activeTab === "product-details" && (
                  <div className="tab-panel">
                    <div className="product-specs">
                      {product.details && product.details.split('\n').map((line, idx) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return null;

                        if (trimmedLine.includes(':')) {
                          const [key, ...valueParts] = trimmedLine.split(':');
                          const value = valueParts.join(':');
                          return (
                            <p key={`detail-${idx}`}>
                              <strong>{key.trim()}:</strong> {value.trim()}
                            </p>
                          );
                        }
                        return (
                          <p key={`detail-${idx}`} dangerouslySetInnerHTML={{ __html: trimmedLine }} />
                        );
                      })}

                      {product.productCode && (
                        <p><strong>Product Code:</strong> {product.productCode}</p>
                      )}
                      {displayedColorName &&
                        displayedColorName !== "Not specified" && (
                          <p><strong>Color:</strong> {displayedColorName}</p>
                        )}

                      {product.type && (
                        <p><strong>Style:</strong> {product.type}</p>
                      )}
                      {product.tags && product.tags.length > 0 && (
                        <p><strong>Work:</strong> {product.tags.join(", ")}</p>
                      )}
                      {product.fabric && product.fabric !== "Not specified" && (
                        <p><strong>Fabric:</strong> {product.fabric}</p>
                      )}
                      {product.categoryType && (
                        <p><strong>Occasion:</strong> {product.categoryType}</p>
                      )}

                    </div>
                  </div>
                )}
                {activeTab === "size-fit" && (
                  <div className="tab-panel">
                    {product.categoryType === "Saree" ? (
                      <>
                        {product.sareeSize && <p>Saree: {product.sareeSize} Mtrs</p>}
                        {product.blouseSize && <p>Blouse: {product.blouseSize} Mtrs</p>}
                        {!product.sareeSize && !product.blouseSize && <p>Free Size</p>}
                      </>
                    ) : (
                      <p>{product.sizeAndFit || "Free Size"}</p>
                    )}
                  </div>
                )}
                {activeTab === "wash-care" && (
                  <div className="tab-panel">
                    {product.materialAndCare && product.materialAndCare !== "Not specified" ? (
                      <p>{product.materialAndCare}</p>
                    ) : (
                      <p>To keep the fabric intact, gently hand wash with mild detergent.</p>
                    )}
                  </div>
                )}
                {activeTab === "shipping" && (
                  <div className="tab-panel">
                    <p>Free shipping on all orders above $100 AUD.</p>
                    <p>Delivery within 5-7 business days.</p>
                    <p>Express delivery available at additional charges.</p>
                  </div>
                )}
              </div>

            </div>

            {/* <div className="accordion-sections">
              <div
                className="accordion-item"
                onClick={() => toggleSection("specifications")}
              >
                <div className="accordion-header">
                  <span>PRODUCT SPECIFICATIONS</span>
                  <span
                    className={`arrow ${
                      activeSection === "specifications" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "specifications" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Product Type: Saree</li>
                      <li>Material: Art Silk</li>
                      <li>Color: Rani Pink</li>
                      <li>Pattern: Zariwork Embroidery</li>
                      <li>Length: 5.5 meters</li>
                      <li>Blouse Piece: Included (0.80 meters)</li>
                      <li>Occasion: Festival, Wedding, Party</li>
                      <li>
                        Package Contents: 1 Saree with Unstitched Blouse Piece
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("shipping")}
              >
                <div className="accordion-header">
                  <span>SHIPPING INFORMATION</span>
                  <span
                    className={`arrow ${
                      activeSection === "shipping" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "shipping" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Free shipping on all orders above ₹999</li>
                      <li>Delivery within 5-7 business days</li>
                      <li>
                        Express delivery available (additional charges apply)
                      </li>
                      <li>
                        Track your order through our website or mobile app
                      </li>
                      <li>Currently delivering to all major cities in India</li>
                      <li>
                        International shipping available to select countries
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("more")}
              >
                <div className="accordion-header">
                  <span>MORE INFORMATION</span>
                  <span
                    className={`arrow ${
                      activeSection === "more" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "more" && (
                  <div className="accordion-content">
                    <ul>
                      <li>
                        Style Tips: Pair with traditional jewelry for a complete
                        look
                      </li>
                      <li>Maintenance: Dry clean only</li>
                      <li>Storage: Keep in a cool, dry place</li>
                      <li>Authenticity: 100% genuine product</li>
                      <li>
                        Quality Check: Passes through 3-step quality check
                      </li>
                      <li>
                        Certification: ISO 9001:2015 certified manufacturer
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("help")}
              >
                <div className="accordion-header">
                  <span>NEED HELP</span>
                  <span
                    className={`arrow ${
                      activeSection === "help" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "help" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Customer Care: 1800-123-4567 (Toll-Free)</li>
                      <li>Email: support@koskii.com</li>
                      <li>Working Hours: Mon-Sat, 10 AM - 7 PM</li>
                      <li>WhatsApp Support: +91 98765 43210</li>
                      <li>Live Chat: Available on website and app</li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("offer")}
              >
                <div className="accordion-header">
                  <span>HOW TO AVAIL THE BUY 2 GET 1 OFFER</span>
                  <span
                    className={`arrow ${
                      activeSection === "offer" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "offer" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Add any 3 products to your cart</li>
                      <li>
                        Discount will be automatically applied at checkout
                      </li>
                      <li>Lowest priced item will be free</li>
                      <li>Offer valid on selected products only</li>
                      <li>Cannot be combined with other offers</li>
                      <li>Valid until stocks last</li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("faq")}
              >
                <div className="accordion-header">
                  <span>FAQ'S</span>
                  <span
                    className={`arrow ${
                      activeSection === "faq" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "faq" && (
                  <div className="accordion-content">
                    <div className="faq-item">
                      <h4>Is blouse piece included?</h4>
                      <p>
                        Yes, an unstitched blouse piece of 0.80 meters is
                        included.
                      </p>
                    </div>
                    <div className="faq-item">
                      <h4>Do you offer customization?</h4>
                      <p>
                        Yes, we offer blouse customization services at
                        additional cost.
                      </p>
                    </div>
                    <div className="faq-item">
                      <h4>What are the payment options?</h4>
                      <p>
                        We accept all major credit/debit cards, UPI, net
                        banking, and COD.
                      </p>
                    </div>
                    <div className="faq-item">
                      <h4>Is exchange available?</h4>
                      <p>
                        Yes, size exchange is available within 7 days of
                        delivery.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="accordion-item"
                onClick={() => toggleSection("return-policy")}
              >
                <div className="accordion-header">
                  <span>RETURN POLICY</span>
                  <span
                    className={`arrow ${
                      activeSection === "return-policy" ? "active" : ""
                    }`}
                  ></span>
                </div>
                {activeSection === "return-policy" && (
                  <div className="accordion-content">
                    <ul>
                      <li>Easy returns within 7 days of delivery</li>
                      <li>Product must be unused with original tags</li>
                      <li>Free pickup for returns</li>
                      <li>Refund processed within 5-7 business days</li>
                      <li>Exchange option available</li>
                      <li>For detailed policy, visit our returns page</li>
                    </ul>
                  </div>
                )}
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* You may also like section */}
      <div className="related-sections">
        <ProductSliderSection
          title="Complete Your Look"
          products={completeLookProducts}
        />
        <ProductSliderSection
          title="Get Obsessed With"
          products={similarProducts}
        />
      </div>
      {/* Recently Viewed section */}
      {recentlyViewed.length > 0 && (
        <ProductSliderSection
          title="Recently Viewed"
          products={recentlyViewed}
        />
      )}

      {wornAdoredProducts.length > 0 && (
        <div className="worn-adored-section">
          <div className="worn-adored-divider"></div>
          <h2>Worn & Adored</h2>
          <div className="worn-adored-grid">
            {wornAdoredProducts.map((item) => (
              <Link
                key={getNormalizedId(item)}
                to={`/product/${item._id || item.id}`}
                className="worn-adored-card"
                onClick={() => window.scrollTo(0, 0)}
              >
                <img
                  src={item.images?.[0]}
                  alt={item.name}
                  loading="lazy"
                />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Product Reviews Section */}
      <div className="worn-adored-divider"></div>
      <ProductReviews productId={product?._id || productId} productName={product?.productName} />


      {/* How to Measure Modal */}
      {showMeasureModal && (
        <div className="measure-modal-overlay" onClick={() => setShowMeasureModal(false)}>
          <div className="measure-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setShowMeasureModal(false)}>&times;</button>
            <div className="measure-modal-body">
              <div className="measure-header">
                <h2>MEASUREMENT CHART</h2>
                <div className="measurement-date"></div>
              </div>

              {product?.measurements && product.measurements.length > 0 ? (
                product.measurements.map((table, tIndex) => (
                  <div key={tIndex} className="measure-table-container mb-8">
                    <h3>{table.title}</h3>
                    <div className="measure-stack">
                      <div className="measure-table-wrap">
                        <table className="full-measure-table">
                          <thead>
                            <tr>
                              {table.headers.map((header, cIndex) => (
                                <th key={cIndex}>{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {table.data.map((row, rIndex) => (
                              <tr key={rIndex}>
                                {row.map((cell, cIndex) => {
                                  // If unit is inches, try to convert numeric values from cm
                                  let displayValue = cell;
                                  if (sizeUnit === "inches" && cIndex !== 0) { // Skip first column (usually descriptor/label)
                                    const numericValue = parseFloat(cell);
                                    if (!isNaN(numericValue)) {
                                      displayValue = (numericValue / 2.54).toFixed(1);
                                    }
                                  }
                                  return <td key={cIndex}>{displayValue}</td>;
                                })}
                              </tr>
                            ))}

                          </tbody>
                        </table>
                      </div>

                      {(table.image || table.description) && (
                        <div className="measure-media">
                          {table.image && (
                            <div className="measure-image-wrap">
                              <img src={table.image} alt={table.title} />
                            </div>
                          )}

                          {table.description && (
                            <div className="measure-instructions">
                              <p>{table.description}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="measure-instructions">
                  <p>Measurements are not available for this product yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Zoom Modal */}
      {showZoomModal && (
        <ZoomModal
          image={displayImages[selectedImage]}
          onClose={() => {
            setShowZoomModal(false);
            zoomLevelRef.current = 1;
          }}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      )}
    </>
  );
};

/* ── Standalone Zoom Modal component ── */
function ZoomModal({ image, onClose, onPrev, onNext }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#111',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="zoom-close-btn"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ zIndex: 10001 }}
      >
        ×
      </button>

      {/* Prev button */}
      <button
        className="zoom-nav-button prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        style={{ zIndex: 10001 }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Image */}
      <img
        src={image}
        alt="Zoomed view"
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          objectFit: 'contain',
          display: 'block',
          borderRadius: 4,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          userSelect: 'none',
          position: 'relative',
          zIndex: 10000,
        }}
      />

      {/* Next button */}
      <button
        className="zoom-nav-button next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        style={{ zIndex: 10001 }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

export default ProductDetail;
